import { trimDecimalZeros } from "@cpg/common";
import { useAmountOrder } from "@/lib/amount-order";
import { cn } from "@/lib/utils";

// Centralizes the native-crypto + USD-equivalent rendering pattern that's
// currently duplicated ad-hoc across pages (e.g. routes/_app.invoices.tsx's
// local formatCrypto/formatFiat). Display-only: the Number(...) cast here
// feeds Intl.NumberFormat for rendering, it is never summed, compared, or
// used to derive another displayed figure.

const USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** Full precision, trailing zeros trimmed — the source of truth for CSV export and the `Amount` hover title. */
export function formatCryptoAmount(amount: string | null, symbol: string): string {
  return amount === null ? "—" : `${trimDecimalZeros(amount)} ${symbol}`;
}

export function formatUsdAmount(amount: string | null): string {
  return amount === null ? "—" : USD_FORMATTER.format(Number(amount));
}

// At least 2 fractional digits (e.g. "100" -> "100.00"), uncapped above
// that so a more-precise fiat amount isn't silently truncated.
const FIAT_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 20,
});

/** Full-string form for CSV export — see `FiatAmount` for the on-screen (currency-code-bold) rendering. */
export function formatFiatAmount(amount: string, currency: string): string {
  return `${FIAT_FORMATTER.format(Number(amount))} ${currency}`;
}

export interface FiatAmountProps {
  amount: string;
  currency: string;
}

export function FiatAmount({ amount, currency }: FiatAmountProps) {
  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      {FIAT_FORMATTER.format(Number(amount))} <span className="font-semibold">{currency}</span>
    </span>
  );
}

// Some assets (e.g. 18-decimal ETH-based tokens) can carry far more
// fractional digits than are useful on screen. On-screen rendering caps at
// DISPLAY_DECIMALS with a trailing "…"; the untruncated value is still one
// hover away via the `title` attribute, never silently dropped.
const DISPLAY_DECIMALS = 8;

/** The numeric part only (no symbol) — `Amount` renders the symbol separately so it can be styled on its own. */
function formatCryptoAmountValue(amount: string): string {
  const trimmed = trimDecimalZeros(amount);
  const [whole, fraction] = trimmed.split(".");
  if (!fraction || fraction.length <= DISPLAY_DECIMALS) return trimmed;
  return `${whole}.${fraction.slice(0, DISPLAY_DECIMALS)}`;
}

export interface AmountProps {
  native: string | null;
  symbol: string;
  usd?: string | null;
  /**
   * Collapses the crypto/USD pairing onto one line ("value SYMBOL · $usd")
   * instead of stacking it on two — for detail pages where the two-line
   * stack reads as oddly tall next to a single label. Tables should leave
   * this at the default (two lines, one value per line), so a column of
   * these stays vertically compact and every row's baseline lines up.
   * No effect when `usd` isn't passed — there's only one line either way.
   */
  inline?: boolean;
}

export function Amount({ native, symbol, usd, inline = false }: AmountProps) {
  const order = useAmountOrder();
  const full = formatCryptoAmount(native, symbol);

  if (native === null) {
    return <span style={{ fontVariantNumeric: "tabular-nums" }}>{full}</span>;
  }

  const value = formatCryptoAmountValue(native);
  const truncated = `${value} ${symbol}` !== full;

  if (usd === undefined) {
    return (
      <span
        style={{ fontVariantNumeric: "tabular-nums" }}
        title={truncated ? full : undefined}
        className={cn(truncated && "cursor-help underline-offset-2")}
      >
        {value} {symbol}
      </span>
    );
  }

  // Crypto amount + its USD-equivalent estimate — same convention as every
  // other USD-paired figure in the app (was previously duplicated ad hoc per
  // call site as a sibling `formatUsdAmount` span; centralized here so every
  // caller gets it for free via `usd`). Which one comes first (and therefore
  // which one is the muted/secondary figure — the styling always follows the
  // primary one, not a fixed field) is a user-controlled display setting
  // (SettingsPopover), not per-call-site. `inline` only changes the layout
  // (one line, "·"-joined, vs. two stacked lines) — the order/muting logic
  // is identical either way.
  const cryptoIsPrimary = order !== "fiat-first";
  const secondaryClass = "text-xs text-muted-foreground";
  const lineClass = inline ? undefined : "block";

  const crypto = (
    <span
      style={{ fontVariantNumeric: "tabular-nums" }}
      title={truncated ? full : undefined}
      className={cn(lineClass, truncated && "cursor-help underline-offset-2", !cryptoIsPrimary && secondaryClass)}
    >
      {value} {symbol}
    </span>
  );

  const usdLine = (
    <span className={cn(lineClass, cryptoIsPrimary && secondaryClass)} style={{ fontVariantNumeric: "tabular-nums" }}>
      {formatUsdAmount(usd)}
    </span>
  );

  const [first, second] = cryptoIsPrimary ? [crypto, usdLine] : [usdLine, crypto];

  if (inline) {
    return (
      <span className="inline-flex items-baseline gap-1.5">
        {first}
        <span aria-hidden className="text-muted-foreground">·</span>
        {second}
      </span>
    );
  }

  return (
    <span>
      {first}
      {second}
    </span>
  );
}

export interface CryptoFiatAmountProps {
  native: string;
  symbol: string;
  fiatAmount: string;
  fiatCurrency: string;
  /** Same one-line-vs-two-line toggle as `Amount`'s `inline` — see its doc comment. */
  inline?: boolean;
}

/**
 * Crypto amount + the invoice's own billed-fiat amount (e.g. the invoices
 * list's "Paid" column) — distinct from `Amount`'s `usd` prop, which is a
 * live USD *estimate* of a native balance, not a stored fiat amount. Same
 * order-follows-the-setting, muted-line-follows-the-order-not-a-fixed-field
 * convention as `Amount` (SettingsPopover), so the two behave identically
 * wherever both appear in the same table.
 */
export function CryptoFiatAmount({ native, symbol, fiatAmount, fiatCurrency, inline = false }: CryptoFiatAmountProps) {
  const order = useAmountOrder();
  const cryptoIsPrimary = order !== "fiat-first";
  const secondaryClass = "text-xs text-muted-foreground";
  const lineClass = inline ? undefined : "block";

  const full = formatCryptoAmount(native, symbol);
  const value = formatCryptoAmountValue(native);
  const truncated = `${value} ${symbol}` !== full;

  const crypto = (
    <span
      style={{ fontVariantNumeric: "tabular-nums" }}
      title={truncated ? full : undefined}
      className={cn(lineClass, truncated && "cursor-help underline-offset-2", !cryptoIsPrimary && secondaryClass)}
    >
      {value} {symbol}
    </span>
  );

  const fiat = (
    <span className={cn(lineClass, cryptoIsPrimary && secondaryClass)} style={{ fontVariantNumeric: "tabular-nums" }}>
      {FIAT_FORMATTER.format(Number(fiatAmount))} {fiatCurrency}
    </span>
  );

  const [first, second] = cryptoIsPrimary ? [crypto, fiat] : [fiat, crypto];

  if (inline) {
    return (
      <span className="inline-flex items-baseline gap-1.5">
        {first}
        <span aria-hidden className="text-muted-foreground">·</span>
        {second}
      </span>
    );
  }

  return (
    <span>
      {first}
      {second}
    </span>
  );
}
